import {
  dateSettings,
  FORMULA_SCENE,
  fieldFindRegExp,
  dateNowRegExp,
  fieldReplaceRegExp,
  numberFields,
  dateFields,
  formulaTypeOptions,
  tableAndFieldConnector,
  fieldConnector
} from './setting'
import { formatDate, convertNumber, getDatePattern } from './_utils'
import formulaFunctions from './index'
import { Interpreter } from 'eval5'
import moment from 'moment'
const interpreter = new Interpreter(formulaFunctions)
const selectTypes = [
  'select',
  'system',
  'dept',
  'user',
  'users',
  'selects',
  'tree-select',
  'select-table-plus',
  'select-table-link'
]
const dateTypes = ['date', 'datetime', 'year', 'month', 'time']

/**
 * 转换老的计算公式以适配新的公式编辑器
 * 因为老的公式中字段直接用的A,B这种单个的单词表示，时间类型变量又是[q1],[m2]这类标识，为了转换时不识别错误
 * 先把老公式中的时间变量转换为特殊的不与字段标识冲突的表示，然后将字段转换为新的表示{{xxx}},再将时间标识转为新版的函数表示
 * @param {Object} tempOperationOptions 表头接口返回的计算字段extend数据 fields需要JSON.parse
 * @param {Array} tableFields 表头接口返回的indexFields字段数据
 * @param {Boolean} isTableHeader 是否是自定义表表头配置计算公式，表头配置的计算公式tableFields需要特殊处理
 * @returns 转换后适用新的计算公式配置的公式
 */
export const convertOldFormulaText = (tempOperationOptions, tableFields, isTableHeader = false) => {
  let { formula, fields } = tempOperationOptions
  // step 1
  formula = formula.replace(/\[[A-Z]+(\d+\])/g, (item, index) => {
    let type = item.replace(/[^A-Z]/g, ''),
      number = item.replace(/[^\d*]/g, ''),
      keyMapsIndex = dateSettings.keyMaps.findIndex((k) => k === type.toUpperCase())
    return `${fieldConnector}${keyMapsIndex}||${number}${fieldConnector}`
  })

  // step 2
  formula = formula.replace(/\[NOW\]|\[now\]/g, (item, index) => `${fieldConnector}666${fieldConnector}`)

  // step 3
  for (let key in fields) {
    let tempField = isTableHeader
      ? tableFields.find((item) => item.aid == fields[key])
      : tableFields.find((item) => item[7]['field_id'] == fields[key])
    if (tempField) formula = formula.replaceAll(key, `{{${isTableHeader ? tempField.name : tempField[1]}}}`)
  }

  // step 4
  formula = formula.replace(new RegExp(`${fieldConnector}(.*?)${fieldConnector}`, 'g'), (item, index) => {
    let value = item.replace(new RegExp(`^${fieldConnector}|${fieldConnector}$`, 'g'), '')
    if (value === '666') return 'DATENOW()'

    let [k, v] = value.split('||')
    return `${dateSettings[dateSettings.keyMaps[k]]['method']}(${v})`
  })
  return formula
}

/**
 * 将计算公式转换为编辑器展示的内容
 * @param {Object} formula 表头接口返回的计算字段extend中formula数据
 * @param {Array} tableFields 表头接口返回的indexFields字段数据
 * @param {Boolean} isTableHeader 是否是自定义表表头配置计算公式，表头配置的计算公式tableFields需要特殊处理
 * @returns 公式编辑器可展示的内容
 */
export const convertNewFormulaToShow = (formula, tableFields = [], isTableHeader = false) =>
  formula.replace(fieldFindRegExp, (item, index) => {
    let value = item.replace(fieldReplaceRegExp, ''),
      [fieldId, fieldName] = value.split(fieldConnector),
      fieldItem = isTableHeader
        ? tableFields.find((item) => item.aid == fieldId)
        : tableFields.find((item) => item[7]['field_id'] == fieldId)
    return fieldItem ? `{{${isTableHeader ? fieldItem.name : fieldItem[1]}}}` : fieldName
  })

/**
 * 将回显的计算公式转为显示格式
 * @param {String} formula 计算公式文本
 * @param {Array} fields 字段数据
 * @returns {String} 用于回显的计算公式文本
 */
export const convertEchoFormulaToShow = (formula, fields) => {
  if (fields) fields = expandMultiTierTableDatas(fields)
  return (formula || '').replace(fieldFindRegExp, (item, index) => {
    let value = item.replace(fieldReplaceRegExp, ''),
      [aliasName, field, fieldName] = value.split(fieldConnector)
    if (fields) {
      let fieldItem = fields.find((v) => v.alias === aliasName && v.field === field)
      fieldName = fieldItem?.fieldName ?? fieldName
    }
    return `{{${fieldName}}}`
  })
}

/**
 * 将计算公式转换为可提交数据
 * @param {String} formulaText 计算公式展示的文本
 * @param {Array} fields 可以参与运算的字段
 * @param {Boolean} [hasMoreTable] 存在多个表的字段
 * @returns {Object} r.fieldsMap 字段变量映射 r.postFormulaText提交的计算公式 r.formulaText展示的计算公式
 */
export const convertNewFormulaToPost = (formulaText, fields, hasMoreTable = false) => {
  if (hasMoreTable) fields = expandMultiTierTableDatas(fields)
  let fieldsMap = {}
  let postFormulaText = formulaText.replace(fieldFindRegExp, (item, index) => {
    let value = item.replace(fieldReplaceRegExp, ''),
      fieldItem = fields.find((field) => field.fieldName === value)
    if (fieldItem) {
      let k = hasMoreTable
        ? `${fieldItem.alias || 0}${fieldConnector}${fieldItem.field}${fieldConnector}${fieldItem.fieldName}`
        : `${fieldItem.fieldId}${fieldConnector}${fieldItem.field}`
      fieldsMap[k] = fieldItem.fieldId
      return `{{${k}}}`
    }
    return item
  })

  return {
    fieldsMap,
    postFormulaText,
    formulaText
  }
}

/**
 * 展开多层表格数据
 * @param {Array} fields 表格和字段数据
 * @returns {Array} 字段数据
 */
export const expandMultiTierTableDatas = (fields) =>
  fields.reduce(
    (allFields, table) =>
      allFields.concat(
        table.tableFields
          ? table.tableFields.map((field) => ({
              ...field,
              fieldName: `${table.name}${tableAndFieldConnector}${field.fieldName}`,
              tableId: table.aid,
              tableName: table.name,
              alias: table.alias
            }))
          : [table]
      ),
    []
  )

/**
 * 老计算公式使用新公式编辑器编辑时将老公式转为新公式的可提交数据
 * @param {String} formulaText 使用函数convertOldFormulaText转化过的计算公式展示文本
 * @param {Array} tableFields 表头接口获取到的数据
 * @returns fieldsMap 字段变量映射 postFormulaText提交的计算公式 formulaText展示的计算公式
 */
export const convertOldFormulaToPost = (formulaText, tableFields) => {
  let fieldsMap = {}
  let postFormulaText = formulaText.replace(fieldFindRegExp, (item, index) => {
    let value = item.replace(fieldReplaceRegExp, ''),
      fieldItem = tableFields.find((field) => field[1] === value)
    if (fieldItem) {
      let k = `${fieldItem[7].field_id}${fieldConnector}${fieldItem[0]}`
      fieldsMap[k] = fieldItem[7].field_id
      return `{{${k}}}`
    }
    return item
  })

  return {
    fieldsMap,
    postFormulaText,
    formulaText
  }
}

/**
 * 将计算公式转换为验证可用的数据
 * @param {String} formulaText 计算公式展示的文本
 * @param {Array} fields 可以参与运算的字段
 * @param {Boolean} [hasMoreTable] 存在多个表的字段
 * @returns 可用于eval5解析计算的公式
 */
export const convertNewFormulaToTest = (formulaText, fields, hasMoreTable = false) => {
  if (hasMoreTable) fields = expandMultiTierTableDatas(fields)
  const nowDate = Math.floor(new Date().getTime() / 1000)
  let errorText = null
  formulaText = formulaText
    .replace(fieldFindRegExp, (item, index) => {
      const value = item.replace(fieldReplaceRegExp, ''),
        fieldItem = fields.find((field) => field.fieldName === value)
      if (fieldItem)
        return numberFields.includes(fieldItem.type) ? 1 : dateFields.includes(fieldItem.type) ? nowDate : '"test"'
      else errorText ??= `字段"${value}"未定义`
      return item
    })
    .replace(dateNowRegExp, () => Math.floor(new Date().getTime() / 1000))
  return {
    formulaText,
    errorText
  }
}

/**
 * 新的计算公式解析
 * @param {String} formulaScene 计算场景 包含表格、详情、表单
 * @param {Object|Array} allColumns 场景为表格时为Object{fieldId: {...fieldinfo}}与其他场景格式不同Array([{...fieldInfo}])
 * @param {Object} formulaField 当前计算的字段配置
 * @param {Object|undefined} record 场景为表格时是当前行的记录数据
 * @returns 计算结果
 */
export const newCalcFormulaParse = (formulaScene, allColumns, formulaField, record) => {
  let sceneIsTable = formulaScene === FORMULA_SCENE.TABLE,
    extendData = !sceneIsTable ? formulaField.extend : formulaField.extra.extend,
    soumulaFieldSourceType = formulaField?.sourceType ?? formulaField?.type,
    formulaText = extendData.formula,
    formulaType = extendData?.formula_type ?? '',
    nowTime = Math.floor(new Date().getTime() / 1000),
    existsNotFindField = false,
    fields = extendData?.fields

  const convertEnterParameters = (n) => (n !== '' && n * 1 == n && (n * 1 + '').indexOf('e') === -1 ? n : `'${n}'`)
  const valueIsEmpty = (v) => Number.isNaN(v) || v === undefined || v === null || v === ''
  formulaText = formulaText.replace(fieldFindRegExp, (item) => {
    const [fieldId, fieldName] = item.replace(fieldReplaceRegExp, '').split(fieldConnector)
    let fieldItem = !sceneIsTable ? allColumns.find((item) => item.field_id == fieldId) : allColumns[fieldId]

    // 如果不是表格场景 并且没有找到对应字段 并且当前计算字段是关联过来的字段 去关联字段里查找field
    if (!sceneIsTable && !fieldItem && soumulaFieldSourceType === 'select-table')
      fieldItem = allColumns.find((item) => item?.select_table_field_id == fieldId)

    // 没取到字段数据 直接返回
    if (!fieldItem) {
      existsNotFindField = true
      return record?.[fieldName] ?? ''
    }

    let fieldType = fieldItem.type ?? fieldItem.draw_type
    let fieldValue = !sceneIsTable ? fieldItem.value : record[fieldItem.field]

    // 如果是关联表字段 需要获取原始字段类型
    const isSelectTableField = ['select-table', 'copy_field'].includes(fieldType) && fieldItem?.original_draw_type
    if (isSelectTableField) fieldType = fieldItem?.original_draw_type

    // 处理各个类型字段的空值
    if (valueIsEmpty(fieldValue)) fieldValue = ''
    if (dateTypes.includes(fieldType) && (fieldValue === 'NOW_DATE' || fieldValue === '0' || fieldValue === 0))
      fieldValue = ''
    if (selectTypes.includes(fieldType) && (fieldValue === '[]' || fieldValue === '0')) fieldValue = ''

    switch (true) {
      case fieldType === 'percentage':
        return convertNumber(fieldValue) / 100
      case numberFields.includes(fieldType):
        return convertNumber(fieldValue)

      // 日期类型统一转化为实践戳参与运算
      case dateFields.includes(fieldType):
        return fieldValue
          ? fieldValue * 1 == fieldValue
            ? fieldValue
            : formatDate(fieldValue, 'TIMESTAMP', getDatePattern(fieldValue)) / 1000
          : convertEnterParameters(fieldValue == 0 ? '' : fieldValue)

      // 计算类型字段 如果是关联的计算字段或者是老的计算公式字段 直接返回数据库存储的值
      case fieldType === 'operation':
        if (isSelectTableField || fieldItem?.extend?.version !== 1) return convertEnterParameters(fieldValue)
        const calcResult = newCalcFormulaParse(formulaScene, allColumns, fieldItem, record)
        return convertEnterParameters(valueIsEmpty(calcResult) ? '' : calcResult) // calcResult * 1 == calcResult ? calcResult : `'${calcResult}'`
      default:
        return convertEnterParameters(fieldValue) // result * 1 == result ? result : `'${result}'`
    }
  })

  // 如果后端返回的右now_time字段 此时公式中的NOWDATE函数使用后端返的值
  if (nowTime) formulaText = formulaText.replace(dateNowRegExp, () => nowTime)

  // 如果公式中含有不存在的字段，直接返回数据库存储的值
  if (existsNotFindField)
    return sceneIsTable ? record[formulaField?.dataIndex ?? formulaField?.field] : formulaField.value
  // return formulaText
  try {
    let returnText = interpreter.evaluate(formulaText)
    returnText !== returnText && (returnText = '')
    if (formulaType && formulaTypeOptions?.[formulaType])
      returnText = moment(returnText * 1000).format(formulaTypeOptions[formulaType])
    return returnText
  } catch (e) {
    let errorText = e.message
    if (errorText.indexOf('is not defined') != -1) errorText = errorText.replaceAll('is not defined', '未定义')
    return ''
  }
}

export const interpreterEvaluate = (formulaText) => {
  try {
    let returnText = interpreter.evaluate(formulaText)
    return returnText
  } catch (e) {
    console.log(e)
    let errorText = e.message
    if (errorText.indexOf('is not defined') != -1) errorText = errorText.replaceAll('is not defined', '未定义')
    return ''
  }
}

// 给jq版用的
if (process?.env?.PUBHISH_JQ_FORMULA) {
  global.newCalcFormulaParse = newCalcFormulaParse
  global.interpreterEvaluate = interpreterEvaluate
}
